import { useQuery } from "@apollo/client/react";
import { GET_REALEFLOW_LOGIN_TOKEN } from '@/housefolios-components/PropertySearch/queries'
import React, {
  useState,
  useEffect,
  useMemo} from 'react'
import { CircularProgress, Typography, Grid, Paper } from '@mui/material'
import withSession from '../Session/withSession'
// Centralized lodash imports - much cleaner!
import { get } from '@/utils/lodash'
import useAffiliate from '@/hooks/useAffiliate'
import { useLocation } from 'react-router'
import PropertySearchUnavailablePage from '@/housefolios-pages/PropertySearchUnavailable'

function RealeflowRedirect(props) {
  const location = useLocation()
  const memberNorm = String(
    props?.session?.me?.activeOrg?.member || '',
  ).toLowerCase()
  const isPropertySearchRetired = [
    'pacemorby',
    'astroflip',
    'pep',
  ].includes(memberNorm)

  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const urlLeadType = urlParams.get('leadType') || 'Leads'

  // Normalize new sidebar labels to Realeflow routes
  function normalizeLeadType(leadType) {
    switch (leadType) {
      case 'Residential':
        return 'Leads'
      case 'Commercial':
        return 'Commercial Leads'
      case 'Find Investors':
        return 'Find Fix & Flippers'
      case 'Commercial Investor Leads':
        return 'Find Fix & Flippers'
      case 'Creative MLS':
        // Fallback to default leads for now
        return 'Leads'
      default:
        return leadType || 'Leads'
    }
  }
  const effectiveLeadType = normalizeLeadType(urlLeadType)
  const [iframeSrc, setIframeSrc] = useState('')
  const [hasRealeflowSession, setHasRealeflowSession] =
    useState(false)
  const [lastRequestedReturnUrl, setLastRequestedReturnUrl] =
    useState(null)

  const { loading, error, data } = useQuery(
    GET_REALEFLOW_LOGIN_TOKEN,
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
      skip: isPropertySearchRetired,
    },
  )

  const [{ affiliate }] = useAffiliate({
    memberValue: props?.session?.me?.activeOrg?.member,
  })

  const returnUrl = useMemo(() => {
    let basePath = '/Marketing/Leads'

    if (effectiveLeadType === 'Find Fix & Flippers') {
      basePath = '/Marketing/Leads/Investors'
    } else if (effectiveLeadType === 'Commercial Leads') {
      basePath = '/Marketing/Leads/Index/Commercial'
    }

    if (urlParams.get('investorPortfolioHash')) {
      let url =
        basePath +
        `/Index/Property#/search?investorPortfolioHash=${urlParams.get('investorPortfolioHash')}`

      // Append investorType if present in the URL
      if (urlParams.get('investorType')) {
        url += `&investorType=${urlParams.get('investorType')}`
      }

      return url
    }

    if (urlParams.get('propertyCompsHash')) {
      return (
        basePath +
        `/Index/Property#/property/${urlParams.get('propertyCompsHash')}`
      )
    }

    if (urlParams.get('investorHash')) {
      return (
        basePath +
        `/Index/Investors#/investor/${urlParams.get('investorHash')}`
      )
    }

    if (urlParams.get('savedSearch')) {
      // Handle saved search parameters
      const savedSearch = urlParams.get('savedSearch')
      const ssid = urlParams.get('ssid')
      return `/Marketing/Leads#/?savedSearch=${savedSearch}${ssid ? `&ssid=${ssid}` : ''}`
    }

    return basePath
  }, [effectiveLeadType, urlParams])

  const realeflowHost = useMemo(() => {
    const activeOrg = props?.session?.me?.activeOrg || {}
    return (activeOrg?.realeflowURL || 'search.propertyedge.pro')
      .replace(/^https?:\/\//, '')
      .trim();
  }, [props?.session?.me?.activeOrg])

  // Load Realeflow in one navigation (token + returnUrl).
  // After the first successful load, subsequent in-app navigation should be
  // direct (no one-time token reuse).
  useEffect(() => {
    if (!data?.getRealeflowLoginToken) return
    if (!realeflowHost) return

    // Once the Realeflow session is established, navigate directly to avoid
    // reusing one-time tokens when switching sub-routes.
    if (hasRealeflowSession) {
      if (lastRequestedReturnUrl === returnUrl) return

      const directUrl = `https://${realeflowHost}${returnUrl}`
      setIframeSrc((prev) => (prev === directUrl ? prev : directUrl))
      setLastRequestedReturnUrl(returnUrl)
      return
    }

    const { UserId, Token } = data.getRealeflowLoginToken
    const logonWithTokenUrl = `https://${realeflowHost}/Account/Account/LogOnWithToken?id=${encodeURIComponent(
      UserId,
    )}&token=${encodeURIComponent(Token)}&returnUrl=${encodeURIComponent(returnUrl)}`

    setIframeSrc((prev) =>
      prev === logonWithTokenUrl ? prev : logonWithTokenUrl,
    )
    setLastRequestedReturnUrl(returnUrl)
  }, [
    data,
    realeflowHost,
    returnUrl,
    hasRealeflowSession,
    lastRequestedReturnUrl,
  ])

  if (isPropertySearchRetired) {
    return <PropertySearchUnavailablePage />
  }

  if (loading)
    return (
      <CircularProgress
        size={80}
        style={{ marginLeft: '50%', marginRight: '50%' }}
      />
    )
  if (error) return <p>Error: {error.message}</p>

  return (
    <div
      style={{
        width: '100%',
        height: '90vh',
        border: 'none',
        alignContent: 'center',
        justifyContent: 'space-around'}}
    >
      {/* Iframe rendering */}
      {iframeSrc ? (
        <iframe
          src={iframeSrc}
          allow="geolocation; microphone"
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="Property Search Tool"
          // sandbox="allow-scripts allow-same-origin"
          onLoad={() => {
            console.log('Iframe loaded successfully')
            setHasRealeflowSession(true)
          }}
          onError={() => console.log('Iframe failed to load')}
        ></iframe>
      ) : (
        <Grid
          container
          direction="column"
          sx={{
            justifyContent: "center",
            alignItems: "center",
            minHeight: '100vh',

            // Light gray background
            backgroundColor: '#f0f2f5',

            padding: 2
          }}>
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              textAlign: 'center',
              maxWidth: 600,
              width: '100%'}}
          >
            <img
              alt="Housefolios"
              src={
                affiliate
                  ? get(
                      affiliate,
                      'sidebar.sidebarMemberFullImage.0.url',
                    ) || '/images/NewLogoBlue.png'
                  : '/images/NewLogoBlue.png'
              }
              style={{
                width: 300,
                marginBottom: 16,
                backgroundColor: '#3b3e66',
                borderRadius: 30,
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'}}
            />
            <Typography variant="h5" gutterBottom>
              We Failed to load the property search
            </Typography>
            <Typography variant="body1">
              Please refresh to try again or contact support.
            </Typography>
          </Paper>
        </Grid>
      )}
    </div>
  );
}

export default withSession(RealeflowRedirect)
